<template>
  <CModal
    :show="isVisible"
    :closeOnBackdrop="false"
    :centered="true"
    color="dark"
    size="xl"
    class="product-edit-modal"
    @update:show="onUpdateShow"
  >
    <template #header>
      <div class="pem-header">
        <div class="pem-header__info">
          <h5 class="pem-header__title">
            {{ isCreateMode ? $t('product.add_new_product') : $t('global.edit') + ': ' + $t('product.product') }}
          </h5>
          <div v-if="itemName" class="pem-header__badges">
            <CBadge color="secondary" class="pem-badge">{{ itemName }}</CBadge>
            <CBadge
              v-if="hasDifferentSupplierOriginalName"
              color="light"
              class="pem-badge pem-badge--renamed"
              :aria-label="$t('product.supplier_original_name')"
              v-c-tooltip="$t('product.supplier_original_name')"
            >
              <CIcon name="cil-arrow-left" class="mr-1" />
              {{ supplierProduct.product.name }}
            </CBadge>
            <CBadge v-if="isPackagedProduct" color="warning" class="pem-badge">
              <CIcon name="cil-layers" /> {{ $t('packaging.packaged') }} x {{ form.packaging.packaged_product_quantity == null ? 1 : form.packaging.packaged_product_quantity }}
            </CBadge>
            <CBadge v-if="isDeleted" color="danger" class="pem-badge">{{ $t('product.deleted') }}</CBadge>
          </div>
        </div>
        <button
          type="button"
          class="pem-header__close"
          :aria-label="modalCloseLabel"
          :title="modalCloseLabel"
          @click="close"
        >
          <CIcon name="cil-x" height="20" />
        </button>
      </div>
    </template>

    <div class="pem-body" role="region" :aria-label="$t('global.edit') + ' ' + $t('product.product')">
      <!-- Loading -->
      <div v-if="loading" class="pem-loading">
        <div class="pem-loading__spinner" />
        <p class="pem-loading__text">{{ $t('global.please_wait') }}</p>
      </div>

      <template v-else>
        <!-- Alerts -->
        <alert-section
          :successAlertMessage="successAlertMessage"
          :dismissSecs="dismissSecs"
          :dismissSuccessAlert="dismissSuccessAlert"
          :errorAlertMessage="errorAlertMessage"
          :showErrorAlert="showErrorAlert"
        />

        <form-summary
          v-if="!isValid && $v.form.$anyDirty"
          class="alert alert-danger mb-3"
          :validator="$v.form"
          :attributes="attributes"
          :messages="localMessages"
        />

        <!-- Info bar -->
        <div v-if="form.id" class="pem-info-bar">
          <span class="pem-info-item">
            <strong>Anyware ID:</strong> #{{ form.id }}
          </span>
          <template v-if="supplierProduct">
            <span class="pem-info-item">
              <strong>{{ $t('global.supplier') }}:</strong> {{ supplierProduct.supplier && supplierProduct.supplier.name }}
            </span>
            <span v-if="hasDifferentSupplierOriginalName" class="pem-info-item">
              <strong>{{ $t('product.supplier_original_name') }}:</strong> {{ supplierProduct.product.name }}
            </span>
            <span class="pem-info-item">
              <strong>{{ $t('product.supplier_product_id') }}:</strong> #{{ supplierProduct.external_product_id }}
            </span>
          </template>
          <span v-if="isPackagedProduct" class="pem-info-item">
            <strong>{{ $t('purchase_history.current_unit') }}:</strong>
            {{ unitPurchasePrice == null ? '-' : $options.filters.toCurrency(unitPurchasePrice, form.price_currency) }}
          </span>
          <span v-if="form.estimated_price && !parseFloat(form.price_purchase)" class="pem-info-item text-info">
            <strong>≈ {{ $t('product.ingredients.estimated_price') }}:</strong>
            {{ form.estimated_price | toCurrency(form.price_currency) }}
          </span>
        </div>

        <!-- Quick toggles -->
        <div v-if="isRestaurant" class="pem-toggles">
          <div v-if="form.menu" class="pem-toggle-item">
            <strong>{{ $t('product.orderable') }}</strong>
            <CSwitch
              :checked.sync="productOrderable"
              :lazy="false"
              :labelOn="$t('global.switch_yes')"
              :labelOff="$t('global.switch_no')"
              color="success"
              shape="pill"
            />
          </div>
          <div class="pem-toggle-item">
            <strong>{{ $t('product.cook_station') }}</strong>
            <CSwitch
              :checked.sync="$v.form.cook_station.$model"
              :lazy="false"
              :labelOn="$t('global.switch_yes')"
              :labelOff="$t('global.switch_no')"
              color="success"
              shape="pill"
            />
          </div>
          <RestaurantStationSelect v-model="form.station_id" class="pem-station-field" :firm-id="selectedFirm.id"
            :cook-station="form.cook_station" :conflict="form.station_conflict" :product-name="form.name || ''" :disabled="submitted" />
        </div>

        <!-- Tabs -->
        <CForm class="pem-form" @submit.prevent>
          <CTabs
            class="pem-tabs"
            :active-tab.sync="activeTab"
            variant="pills"
          >
            <!-- ═══════════════ TAB: Basic Settings ═══════════════ -->
            <CTab active>
              <template slot="title">
                <CIcon name="cil-task" /> <span class="pem-tab-label">{{ $t('product.basic_settings') }}</span>
              </template>

              <div class="pem-section">
                <div class="pem-row">
                  <CInput
                    class="pem-field"
                    :label="$t('product.product_name')"
                    :placeholder="$t('product.product_name')"
                    :lazy="false"
                    :value.sync="$v.form.name.$model"
                    :isValid="checkIfValid('name')"
                    :invalidFeedback="$t('validation.required_min_length', { length: '2' })"
                  />
                  <CInput
                    class="pem-field"
                    :label="$t('product.gtin')"
                    :placeholder="$t('product.gtin')"
                    :lazy="false"
                    :value.sync="$v.form.gtin.$model"
                  />
                </div>

                <div class="pem-row">
                  <CInput
                    class="pem-field"
                    :label="$t('product.product_name_short')"
                    :placeholder="$t('product.product_name_short')"
                    :lazy="false"
                    :value.sync="$v.form.name_short.$model"
                  />
                  <div class="pem-field" />
                </div>

                <!-- Description -->
                <div class="form-group w-100">
                  <label class="pem-label">{{ $t('product.description') }}</label>
                  <quill-editor
                    v-if="editorReady"
                    ref="quill_editor_d"
                    style="min-height: 80px;"
                    v-model="$v.form.description_short.$model"
                    :options="{ ...editorOption, placeholder: $t('product.description') }"
                  />
                </div>

                <!-- Menu & Cashpad (Restaurant only) -->
                <div v-if="isRestaurant" class="pem-row">
                  <div class="form-group pem-field">
                    <label class="pem-label">{{ $t('menu.menu') }}</label>
                    <v-select
                      class="v-select-menu-products"
                      :searchable="false"
                      :options="allMenus"
                      v-model="$v.form.menu.$model"
                      label="name"
                      :placeholder="$t('global.select_menu')"
                    >
                      <template v-slot:option="menu">
                        <span>{{ menu.name }} <strong>({{ menu.products_count }})</strong></span>
                      </template>
                      <span slot="no-options">{{ $t('global.no_items_found') }}</span>
                    </v-select>
                  </div>

                  <div class="form-group pem-field">
                    <label class="pem-label">{{ $t('cashpad.product') }}</label>
                    <v-select
                      class="v-select-menu-products"
                      :searchable="true"
                      :options="cashpadProducts"
                      v-model="$v.form.cashpad_product.$model"
                      label="label"
                      :placeholder="$t('global.select')"
                      :selectable="(cp) => cp.product_id === null"
                    >
                      <template v-slot:option="cp">
                        <span>{{ cp.label }}</span>
                      </template>
                      <span slot="no-options">{{ $t('global.no_items_found') }}</span>
                    </v-select>
                  </div>
                </div>

                <!-- Warehouse -->
                <section class="pem-warehouse">
                  <div class="pem-warehouse__header">
                    <h6 class="pem-warehouse__title">
                      <CIcon name="cil-object-group" height="18" />
                      {{ $t('warehouse.warehouse') }}
                    </h6>
                    <div v-if="isRestaurant" class="pem-toggle-item">
                      <strong>{{ $t('packaging.packaged_product') }}</strong>
                      <CSwitch
                        :checked.sync="isPackagedProduct"
                        :disabled="isSupplierManagedPackaging"
                        :lazy="false"
                        :labelOn="$t('global.switch_yes')"
                        :labelOff="$t('global.switch_no')"
                        color="success"
                        shape="pill"
                      />
                    </div>
                  </div>

                  <div class="pem-warehouse__grid">
                    <div class="pem-warehouse__column pem-warehouse__column--operations">
                      <div
                        v-if="isSuppliedProductType"
                        class="pem-warehouse-panel pem-warehouse-panel--default pem-default-receiving"
                      >
                        <div class="pem-warehouse-panel__header">
                          <div>
                            <span>{{ $t('warehouse.default_stock_warehouse') }}</span>
                            <small>{{ $t('warehouse.default_stock_warehouse_help') }}</small>
                          </div>
                        </div>
                        <v-select
                          class="v-select-menu-products"
                          :searchable="true"
                          :options="warehouses"
                          v-model="defaultReceivingWarehouse"
                          label="name"
                          :placeholder="$t('warehouse.select_warehouse')"
                          :disabled="warehouses.length === 0"
                        >
                          <template v-slot:option="warehouse">
                            <span class="pem-warehouse-option">
                              <!-- <CIcon name="cil-object-group" /> -->
                              <span>{{ warehouse.name }}</span>
                              <CBadge v-if="warehouse.kind === 'central'" color="primary">
                                {{ $t('warehouse.central') }}
                              </CBadge>
                              <!-- <CBadge :color="warehouse.kind === 'central' ? 'primary' : 'info'">
                                {{ warehouse.kind === 'central' ? $t('warehouse.central') : $t('warehouse.sub') }}
                              </CBadge> -->
                            </span>
                          </template>
                          <template v-slot:selected-option="warehouse">
                            <span class="pem-warehouse-option">
                              <!-- <CIcon name="cil-object-group" /> -->
                              <span>{{ warehouse.name }}</span>
                              <CBadge :color="warehouse.kind === 'central' ? 'primary' : 'info'">
                                {{ warehouse.kind === 'central' ? $t('warehouse.central') : $t('warehouse.sub') }}
                              </CBadge>
                            </span>
                          </template>
                          <span slot="no-options">{{ $t('warehouse.no_warehouse_found') }}</span>
                        </v-select>
                        <div v-if="productWarehouses.length" class="pem-default-receiving__warehouses">
                          <span
                            v-for="warehouse in productWarehouses"
                            :key="warehouse.id"
                            class="pem-default-receiving__warehouse-chip"
                            :class="{ 'pem-default-receiving__warehouse-chip--default': isDefaultReceivingWarehouse(warehouse) }"
                          >
                            <CIcon name="cil-object-group" />
                            <span class="pem-default-receiving__warehouse-name">{{ warehouse.name }}</span>
                            <strong>{{ warehousePiece(warehouse) }}</strong>
                            <CBadge v-if="isDefaultReceivingWarehouse(warehouse)" color="primary">
                              {{ $t('warehouse.default_stock_short') }}
                            </CBadge>
                          </span>
                        </div>
                      </div>

                      <div class="pem-warehouse-panel pem-warehouse-panel--department">
                        <!-- Order Department -->
                        <div class="form-group w-100 mb-0">
                          <label>{{ $t('warehouse.order_department') }}:</label>
                          <div class="od-group">
                            <div class="form-check form-check-inline">
                              <input class="form-check-input d-none" type="radio" name="pem_department" id="pem_od_food" value="1" v-model="form.department_id">
                              <label class="form-check-label" for="pem_od_food">
                                <CIcon name="cil-burger" height="20" /> {{ $t('department.order.food') }}
                              </label>
                            </div>
                            <div class="form-check form-check-inline">
                              <input class="form-check-input d-none" type="radio" name="pem_department" id="pem_od_beverage" value="2" v-model="form.department_id">
                              <label class="form-check-label" for="pem_od_beverage">
                                <CIcon name="cil-coffee" height="20" /> {{ $t('department.order.beverage') }}
                              </label>
                            </div>
                            <div class="form-check form-check-inline">
                              <input class="form-check-input d-none" type="radio" name="pem_department" id="pem_od_service" value="3" v-model="form.department_id">
                              <label class="form-check-label" for="pem_od_service">
                                <CIcon name="cil-puzzle" height="20" /> {{ $t('department.order.service') }}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <SupplierPurchaseSummary
                        v-if="supplierPurchase && supplierPurchase.order_unit"
                        class="pem-warehouse-purchase"
                        :purchase="supplierPurchase"
                        :compact="false"
                        :show-unit-badge="!isSupplierManagedPackaging"
                      >
                        <small v-if="isSupplierManagedPackaging" class="pem-warehouse-purchase__help text-muted">
                          {{ $t('supplier_purchase.packaging_sync_help') }}
                        </small>
                      </SupplierPurchaseSummary>
                    </div>

                    <div class="pem-warehouse__column pem-warehouse__column--product">
                      <!-- Net content and packaging share the same stock basis. -->
                      <div class="pem-warehouse-panel pem-warehouse-panel--content">
                        <div class="pem-warehouse-panel__header">
                          <div>
                            <span>{{ $t('product.net_content_warehouse') }}</span>
                          </div>
                        </div>
                        <div class="pem-row pem-row--fluid">
                          <CInput
                            class="pem-field"
                            :label="$t('product.net_content_warehouse')"
                            :placeholder="$t('product.net_content')"
                            :lazy="false"
                            :value.sync="$v.form.net_content.$model"
                            type="number"
                            step="0.01"
                            pattern="^\d+(?:\.\d{1,2})?$"
                          />
                          <CSelect
                            class="pem-field"
                            :label="$t('global.measurement_unit')"
                            :placeholder="$t('global.please_select')"
                            :lazy="false"
                            :value.sync="$v.form.net_content_unit_code.$model"
                            :options="measureCodes"
                          />
                        </div>
                      </div>

                      <div v-if="isPackagedProduct" class="pem-packaging pem-warehouse-panel">
                        <h6 class="pem-packaging__title">
                          <CIcon name="cil-layers" height="16" />
                          {{ $t('packaging.packaging') }}
                        </h6>
                        <div class="pem-row">
                          <CSelect
                            class="pem-field"
                            :label="$t('packaging.packaging_type_code')"
                            :placeholder="$t('global.please_select')"
                            :lazy="false"
                            :value.sync="$v.form.packaging.packaging_type_code.$model"
                            :options="$globalVar.packagingTypes"
                            :disabled="isSupplierManagedPackaging"
                          />
                          <CInput
                            class="pem-field"
                            :label="$t('packaging.packaged_product_quantity')"
                            placeholder="0.00"
                            :lazy="false"
                            :value.sync="$v.form.packaging.packaged_product_quantity.$model"
                            :readonly="isSupplierManagedPackaging"
                            type="number"
                            step="0.01"
                            pattern="^\d+(?:\.\d{1,2})?$"
                          />
                        </div>
                        <div class="form-group mb-0">
                          <span class="pem-label">{{ $t('purchase_history.current_unit') }}</span>
                          <h5 class="mb-0">{{ unitPurchasePrice == null ? '-' : $options.filters.toCurrency(unitPurchasePrice, form.price_currency) }}</h5>
                        </div>

                        <!-- Packaging Summary -->
                        <div v-if="form.packaging.packaged_product_quantity > 0" class="pem-packaging-summary">
                          <h6 class="pem-packaging-summary__title">
                            <CIcon name="cil-info" height="14" />
                            {{ $t('packaging.package_summary') }}
                          </h6>
                          <div class="pem-packaging-summary__grid">
                            <div class="pem-packaging-summary__item">
                              <span class="pem-packaging-summary__value">
                                1 {{ packagingTypeName }} = {{ form.packaging.packaged_product_quantity }} {{ $t('global.piece') }}
                              </span>
                            </div>
                            <div v-if="form.net_content && form.net_content_unit_code" class="pem-packaging-summary__item">
                              <span class="pem-packaging-summary__label">{{ $t('packaging.total_net_content') }}</span>
                              <span class="pem-packaging-summary__value">
                                {{ form.packaging.packaged_product_quantity }} &times; {{ form.net_content }} {{ unitCodeLabel(form.net_content_unit_code) }}
                                = <strong>{{ totalNetContent }} {{ unitCodeLabel(form.net_content_unit_code) }}</strong>
                              </span>
                            </div>
                            <div v-if="form.net_content && form.net_content_unit_code && totalNetContentConverted" class="pem-packaging-summary__item">
                              <span class="pem-packaging-summary__value text-muted">
                                ≈ {{ totalNetContentConverted }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-else class="pem-warehouse-panel pem-warehouse-panel--stock">
                        <!-- Stock Unit -->
                        <div class="form-group mb-0">
                          <label>{{ $t('warehouse.stock_unit') }}:</label>
                          <div class="mcode-group">
                            <div v-for="mcode in stockMeasureCodes" :key="mcode.value" class="form-check form-check-inline">
                              <input class="form-check-input d-none" type="radio" name="pem_mcode" :id="`pem_mcode_${mcode.value}`" :value="mcode.value" v-model="form.stock_unit_code">
                              <label class="form-check-label" :for="`pem_mcode_${mcode.value}`">{{ mcode.label }}</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Prices ═══════════════ -->
            <CTab>
              <template slot="title">
                <CIcon name="cil-dollar" /> <span class="pem-tab-label">{{ $t('product.prices') }}</span>
              </template>

              <div class="pem-section">
                <div class="pem-row">
                  <CSelect
                    class="pem-field"
                    :label="$t('product.currency')"
                    :placeholder="$t('global.please_select')"
                    :lazy="false"
                    :value.sync="$v.form.price_currency.$model"
                    :options="$globalVar.currency"
                    :isValid="checkIfValid('price_currency')"
                  />

                  <CInput
                    class="pem-field"
                    :label="$t('product.price_amount')"
                    placeholder="0.00"
                    :lazy="false"
                    :value.sync="$v.form.price_amount.$model"
                    type="number"
                    step="0.01"
                    pattern="^\d+(?:\.\d{1,2})?$"
                    :isValid="checkIfValid('price_amount')"
                    @blur="formatPriceField('price_amount')"
                  />

                  <div v-if="!isCreateMode" class="pem-field pem-purchase-cost">
                    <span class="pem-label">{{ $t('product.last_purchase_price') }}</span>
                    <strong class="pem-purchase-cost__value">
                      {{ form.price_purchase == null || form.price_purchase === '' ? '-' : $options.filters.toCurrency(form.price_purchase, form.price_currency) }}
                    </strong>
                    <small class="text-muted d-block">{{ $t('product.last_purchase_price_help') }}</small>
                  </div>
                </div>

                <div class="pem-row">
                  <CSelect
                    class="pem-field"
                    :label="$t('product.tax')"
                    :placeholder="$t('global.please_select')"
                    :lazy="false"
                    :value.sync="$v.form.tax.$model"
                    :options="$globalVar.tax"
                  />

                  <CSelect
                    class="pem-field"
                    :label="$t('product.tax_delivery')"
                    :placeholder="$t('global.please_select')"
                    :lazy="false"
                    :value.sync="$v.form.tax_delivery.$model"
                    :options="$globalVar.tax"
                  />

                  <div class="pem-field" />
                </div>

                <!-- Specific Price -->
                <section class="pem-specific-price">
                  <h6 class="pem-section-title">
                    <CIcon name="cil-money" height="18" />
                    {{ $t('product.specific_price.specific_price') }}
                  </h6>
                  <div class="pem-row">
                    <CSelect
                      class="pem-field"
                      :label="$t('product.specific_price.reduction_type')"
                      :placeholder="$t('global.please_select')"
                      :lazy="false"
                      :value.sync="$v.form.specific_price.reduction_type.$model"
                      :options="[
                        { value: 'amount', label: $v.form.price_currency.$model },
                        { value: 'percentage', label: '%' },
                      ]"
                      :isValid="checkIfValid('specific_price.reduction_type')"
                    />
                    <CInput
                      class="pem-field"
                      :label="$t('product.specific_price.reduction')"
                      :placeholder="$t('product.specific_price.reduction')"
                      :lazy="false"
                      :value.sync="$v.form.specific_price.reduction.$model"
                      type="number"
                      step="0.01"
                      pattern="^\d+(?:\.\d{1,2})?$"
                      :isValid="checkIfValid('specific_price.reduction')"
                      @blur="formatSpecificPriceReduction"
                    />
                  </div>
                  <div class="pem-row">
                    <CInput
                      class="pem-field"
                      :label="$t('product.specific_price.from')"
                      :lazy="false"
                      :value.sync="$v.form.specific_price.from.$model"
                      type="date"
                      :isValid="checkIfValid('specific_price.from')"
                    />
                    <CInput
                      class="pem-field"
                      :label="$t('product.specific_price.to')"
                      :lazy="false"
                      :value.sync="$v.form.specific_price.to.$model"
                      type="date"
                      :min="minDate"
                      :isValid="checkIfValid('specific_price.to')"
                    />
                  </div>
                </section>

                <purchase-price-history
                  v-if="isRestaurant"
                  :purchase_prices="purchasePrices"
                  :current_purchase_price_amount="form.price_purchase"
                  :weighted_purchase_price_amount="weightedPurchasePrice"
                  :unit_statistics="purchaseUnitStatistics"
                  :currency="form.price_currency"
                  :packaged_quantity="form.packaging && form.packaging.packaged_product_quantity"
                />
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Product Image ═══════════════ -->
            <CTab>
              <template slot="title">
                <CIcon name="cil-spa" /> <span class="pem-tab-label">{{ $t('product.product_image') }}</span>
              </template>

              <div class="pem-section">
                <CMedia
                  :aside-image-props="{
                    src: previewImage,
                    shape: 'rounded',
                    width: '100%',
                    height: '120px'
                  }"
                >
                  <p class="mb-2 font-weight-bold">{{ $t('product.product_image') }}:</p>
                  <CInput
                    :value.sync="$v.form.cover_image.$model"
                    type="hidden"
                    class="mb-0"
                  />
                  <label for="pem_img" class="btn btn-info mb-0">{{ $t('global.browse') }}</label>
                  <CInputFile
                    id="pem_img"
                    :class="!checkIfValid('cover_image') ? 'is-invalid' : ''"
                    style="display: none"
                    @change="onFileChanged"
                  />
                  <div v-if="!checkIfValid('cover_image')" class="invalid-feedback">
                    <div class="error" v-if="!$v.form.cover_image.required">
                      <span style="font-size: 1.2rem;">⚠️</span> {{ $t('validation.required_image') }}
                    </div>
                  </div>
                </CMedia>
                <CLink
                  class="small mt-2 text-danger"
                  @click="() => { selectedImage = form.cover_image = null; previewImage = noProductImage; }"
                >
                  <CIcon name="cil-trash" /> {{ $t('global.remove_image') }}
                </CLink>
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Features ═══════════════ -->
            <CTab>
              <template slot="title">
                <CIcon name="cil-star" /> <span class="pem-tab-label">{{ $t('product.features') }}</span>
              </template>

              <div class="pem-section">
                <div v-if="isReplicated" class="alert alert-info border-left-0 mb-3" role="alert">
                  <h6 class="alert-heading">⚠️{{ $t('info.product_features_cannot_be_updated') }}</h6>
                  <span v-html="$t('info.product_feature_updates_are_not_allowed')"></span>
                </div>

                <div :class="isReplicated ? 'disable-item' : ''">
                  <!-- Dietary -->
                  <div class="pem-feature-grid">
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.vegetarian') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.vegetarian.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.vegan') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.vegan.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.lactose_free') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.lactose_free.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.gluten_free') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.gluten_free.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                  </div>

                  <div class="pem-feature-grid mt-3">
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.bio_mark') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.bio_mark.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.safe_in_pregnancy') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.safe_in_pregnancy.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item">
                      <label class="pem-label">{{ $t('product.safe_for_drive') }}</label>
                      <CSwitch :checked.sync="$v.form.feature.safe_for_drive.$model" :labelOn="$t('global.switch_yes')" :labelOff="$t('global.switch_no')" color="success" shape="pill" :lazy="false" />
                    </div>
                    <div class="pem-feature-item" />
                  </div>

                  <hr />

                  <div class="pem-row">
                    <CTextarea
                      class="pem-field"
                      :label="$t('product.alcohol_content')"
                      :lazy="false"
                      :value.sync="$v.form.feature.contains.$model"
                      rows="2"
                    />
                    <CInput
                      class="pem-field"
                      type="number"
                      min="0"
                      :label="$t('product.age_restriction')"
                      :placeholder="$t('product.age_restriction_placeholder')"
                      :description="$t('product.age_restriction_description')"
                      :lazy="false"
                      :value.sync="$v.form.feature.age_restricted.$model"
                    />
                  </div>

                  <div class="pem-row">
                    <CTextarea
                      class="pem-field"
                      :label="$t('product.ingredient')"
                      :lazy="false"
                      :value.sync="$v.form.feature.ingredient.$model"
                      rows="3"
                    />
                    <CTextarea
                      class="pem-field"
                      :label="$t('product.storage_instructions')"
                      :lazy="false"
                      :value.sync="$v.form.feature.storage_instructions.$model"
                      rows="3"
                    />
                  </div>

                  <!-- Allergens -->
                  <div class="form-group w-100 mb-3">
                    <label class="pem-label">{{ $t('product.allergen_information') }}</label>
                    <v-select
                      class="v-select-style"
                      v-model="form.feature.allergen_information"
                      :options="allergenGroups"
                      :reduce="allergen => allergen.value"
                      :label="lang"
                      :placeholder="$t('global.please_select')"
                      multiple
                    >
                      <template v-slot:option="allergen">
                        <span>{{ allergen[lang] }}</span>
                      </template>
                    </v-select>
                    <small class="form-text text-muted">{{ $t('product.allergen_description') }}</small>
                  </div>

                  <hr />

                  <!-- Nutritional values -->
                  <div class="pem-row pem-row--quad">
                    <CInput class="pem-field" :label="$t('product.energy_cal')" :lazy="false" :value.sync="$v.form.feature.energy_cal.$model" />
                    <CInput class="pem-field" :label="$t('product.energy_j')" :lazy="false" :value.sync="$v.form.feature.energy_j.$model" />
                    <CInput class="pem-field" :label="$t('product.sugars')" :lazy="false" :value.sync="$v.form.feature.sugars.$model" />
                    <CInput class="pem-field" :label="$t('product.salt_equivalent')" :lazy="false" :value.sync="$v.form.feature.salt_equivalent.$model" />
                  </div>

                  <div class="pem-row pem-row--quad">
                    <CInput class="pem-field" :label="$t('product.fat')" :lazy="false" :value.sync="$v.form.feature.fat.$model" />
                    <CInput class="pem-field" :label="$t('product.saturated_fat')" :lazy="false" :value.sync="$v.form.feature.saturated_fat.$model" />
                    <CInput class="pem-field" :label="$t('product.protein')" :lazy="false" :value.sync="$v.form.feature.protein.$model" />
                    <CInput class="pem-field" :label="$t('product.carbohydrate')" :lazy="false" :value.sync="$v.form.feature.carbohydrate.$model" />
                  </div>

                  <div class="pem-row pem-row--half">
                    <CInput class="pem-field" :label="$t('product.fibre')" :lazy="false" :value.sync="$v.form.feature.fibre.$model" />
                    <CInput class="pem-field" :label="$t('product.co2')" :lazy="false" :value.sync="$v.form.feature.co2.$model" />
                  </div>

                  <hr />

                  <!-- FBO -->
                  <div class="pem-row">
                    <CTextarea class="pem-field" :label="$t('product.fbo')" :lazy="false" :value.sync="$v.form.feature.fbo.$model" rows="2" />
                    <CTextarea class="pem-field" :label="$t('product.fbo_address')" :lazy="false" :value.sync="$v.form.feature.fbo_address.$model" rows="2" />
                  </div>
                </div>
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Ingredients (Restaurant) ═══════════════ -->
            <CTab v-if="isRestaurant" :disabled="isSupplierProduct">
              <template slot="title">
                <CIcon name="cil-chart-pie" /> <span class="pem-tab-label">{{ $t('product.ingredients.ingredients') }}</span>
              </template>

              <div class="pem-section">
                <product-ingredients
                  v-if="activeTab === restaurantTabIndex('ingredients')"
                  :productId="form.id"
                  :ingredients.sync="form.product_ingredients"
                  :measure_codes="measureCodes"
                  :currency="form.price_currency"
                  :parentNetContent="form.net_content"
                  :parentNetContentUnitCode="form.net_content_unit_code"
                  @open-product="openNestedProduct"
                />
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Extras (Restaurant) ═══════════════ -->
            <CTab v-if="isRestaurant">
              <template slot="title">
                <CIcon name="cil-playlist-add" /> <span class="pem-tab-label">{{ $t('extra.extras') }}</span>
              </template>

              <div class="pem-section">
                <div class="pem-extras-list">
                  <div v-for="extra_group in allExtras" :key="extra_group.id" class="form-group w-100">
                    <section v-if="extra_group.extras.length > 0" class="pem-extra-group">
                      <h6 class="pem-extra-group__title">{{ extra_group.name }}</h6>
                      <ul class="pem-extra-list" :style="'max-height: ' + (extra_group.extras.length > 5 ? extra_group.extras.length : 5) / 2 + 'rem;'">
                        <li v-for="extra in extra_group.extras" :key="extra.id" :class="{ 'pem-extra--disabled': extra.orderable === false, 'pem-extra--product-deleted': extra.product && extra.product.deleted_at }">
                          <label class="pem-extra-label">
                            <input type="checkbox" v-model="form.selectedExtras" :value="extra.id" />
                            <span>
                              {{ extra.name }} [{{ extra.price_amount | toCurrency('EUR') }}]
                              <CBadge v-if="extra.orderable === false" color="warning" class="ml-1" size="sm">
                                {{ $t('product.not_orderable') }}
                              </CBadge>
                              <CBadge v-if="extra.product && extra.product.deleted_at" color="danger" class="ml-1 pem-extra-deleted-badge">
                                {{ $t('product.deleted') }}
                              </CBadge>
                            </span>
                          </label>
                        </li>
                      </ul>
                    </section>
                  </div>
                  <strong v-if="allExtras.every(g => g.extras.length === 0)">{{ $t('global.no_items_found') }}</strong>
                </div>
              </div>
            </CTab>

            <!-- ═══════════════ TAB: Options (Restaurant) ═══════════════ -->
            <CTab v-if="isRestaurant">
              <template slot="title">
                <CIcon name="cil-options" /> <span class="pem-tab-label">{{ $t('product.options.options') }}</span>
              </template>

              <div class="pem-section">
                <product-options
                  :options.sync="form.product_options"
                  :all_options.sync="allOptions"
                  :all_option_groups.sync="allOptionGroups"
                  :measure_codes="measureCodes"
                  @open-product="openNestedProduct"
                />
              </div>
            </CTab>

            <CTab v-if="isRestaurant" :disabled="!isSupplierProduct">
              <template slot="title">
                <CIcon name="cil-link" /> <span class="pem-tab-label">{{ $t('product.equivalent_products') }}</span>
              </template>

              <div class="pem-section">
                <product-equivalents
                  v-if="activeTab === restaurantTabIndex('equivalents') && form.id"
                  :productId="form.id"
                  :firmId="selectedFirm.id"
                />
              </div>
            </CTab>
          </CTabs>

          <!-- Tab loading overlay -->
          <transition name="pem-fade">
            <div v-if="tabLoading" class="pem-tab-loading">
              <div class="pem-loading__spinner" />
            </div>
          </transition>
        </CForm>
      </template>

      <mc-spinner :opacity="0.8" v-show="submitted" />
    </div>

    <template #footer>
      <div class="pem-footer" :class="{ 'disable-item': submitted || tabLoading || loading }">
        <div class="pem-footer__actions">
          <CButton
            color="primary"
            :disabled="submitted"
            @click="update"
          >
            <span v-if="isDeleted">{{ $t('global.undelete') }}</span>
            <template v-else>
              <CIcon name="cil-send" /> <span>{{ $t('global.submit') }}</span>
            </template>
          </CButton>
          <CButton color="link" @click="close">
            {{ cancelButtonLabel }}
          </CButton>
        </div>

        <div v-if="!isCreateMode && [1, 3, 43].includes(auth_user.user.id)" class="pem-footer__danger">
          <CButton size="sm" color="danger" variant="ghost" @click="deleteItem">
            <CIcon name="cil-trash" /> {{ $t('global.delete') }}
          </CButton>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>
import RestaurantStationSelect from '@/components/RestaurantStationSelect.vue';
import SupplierPurchaseSummary from '@/components/SupplierPurchaseSummary.vue';
import { purchaseUnitStatistics as mapPurchaseUnitStatistics } from '@/helper/historicalPurchasePrices';

import api from "@/api";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";

import ProductIngredients from "@/components/ProductIngredients";
import ProductOptions from "@/components/ProductOptions";
import ProductEquivalents from "@/components/ProductEquivalents";
import PurchasePriceHistory from "@/components/PurchasePriceHistory";

import allergen_groups from "@/helper/allergen_groups.js";
import {
  getUnitCodeLabel as _getUnitCodeLabel,
  formatReadableQuantity as _formatReadableQuantity,
} from "@/helper/unitConversions";

export default {
  name: "ProductEditModal",
  components: {
    SupplierPurchaseSummary,
    RestaurantStationSelect,
    vSelect,
    quillEditor,
    ProductIngredients,
    ProductOptions,
    ProductEquivalents,
    PurchasePriceHistory,
  },
  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      tabLoading: false,
      submitted: false,
      editorReady: false,

      form: this.initForm(),

      itemName: "",
      isReplicated: false,
      isDeleted: false,

      selectedImage: undefined,
      previewImage: undefined,

      measureCodes: [],
      allMenus: [],
      allExtras: [],
      allOptions: [],
      allOptionGroups: [],
      cashpadProducts: [],
      warehouses: [],

      loadedTabs: {
        menus: false,
        cashpadProducts: false,
        warehouses: false,
        extras: false,
        options: false,
        optionGroups: false,
      },

      supplierProduct: null,
      supplierPurchase: null,
      suppliedProduct: null,
      defaultReceivingWarehouse: null,
      initialDefaultReceivingWarehouseId: null,
      purchasePrices: null,
      weightedPurchasePrice: null,
      purchaseUnitStatistics: {},

      successAlertMessage: {},
      dismissSecs: 2,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      activeTab: 0,
      navigationStack: [],
      restoringNavigationSnapshot: false,

      editorOption: {
        theme: "bubble",
        modules: {
          toolbar: [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
          ],
        },
      },
    };
  },

  validations() {
    return {
      form: {
        gtin: {},
        category_default_id: {},
        department_id: {},
        sku: {},
        ean: {},
        name: { required, minLength: minLength(2) },
        name_short: {},
        description_short: {},
        description: {},

        price_amount: {
          required: (value) => value !== null && value !== undefined && value !== "",
        },
        price_purchase: {},
        price_currency: { required },
        tax: {},
        tax_delivery: {},

        net_content: {},
        net_content_unit_code: {},
        stock_unit_code: { required },

        active: {},
        cook_station: {},
        categories: {},
        menu: {},
        images: {},

        cover_image: {
          required: (fileName) => {
            if (!fileName) return true;
            var filename_arr = fileName.split(".");
            var file_ext = filename_arr[filename_arr.length - 1];
            return (
              ["01", "jpeg", "jpg", "png", "gif", "webp"].indexOf(file_ext) !== -1 ||
              fileName.endsWith("01") ||
              fileName.startsWith("https://firebasestorage.googleapis.com")
            );
          },
        },

        feature: {
          vegetarian: {},
          vegan: {},
          lactose_free: {},
          gluten_free: {},
          bio_mark: {},
          safe_in_pregnancy: {},
          safe_for_drive: {},
          age_restricted: {},
          ingredient: {},
          contains: {},
          storage_instructions: {},
          fbo: {},
          fbo_address: {},
          allergen_information: {},
          energy_cal: {},
          energy_j: {},
          sugars: {},
          fat: {},
          saturated_fat: {},
          carbohydrate: {},
          protein: {},
          salt_equivalent: {},
          fibre: {},
          co2: {},
        },

        specific_price: {
          reduction_type: {
            required: (value) => {
              if (this.form.specific_price.reduction && this.form.specific_price.from) {
                return value !== null;
              }
              return true;
            },
          },
          reduction: {
            required: (value) => {
              if (this.form.specific_price.reduction_type && this.form.specific_price.from) {
                return value !== null;
              }
              return true;
            },
          },
          from: {
            required: (value) => {
              if (this.form.specific_price.reduction_type && this.form.specific_price.reduction) {
                return value !== null;
              }
              return true;
            },
          },
          to: {
            required: (value) => {
              if (this.form.specific_price.from && value) {
                return value > this.form.specific_price.from;
              }
              return true;
            },
          },
        },

        packaging: {
          packaging_type_code: {},
          package_product_id: {},
          packaged_product_quantity: {},
        },

        cashpad_product: {},
      },
    };
  },

  computed: {
    lang() {
      return localStorage.getItem("lang");
    },
    productId() {
      return this.$store.state.productEditModalId;
    },
    isCreateMode() {
      return this.productId === "new";
    },
    unitPurchasePrice() {
      if (!this.isCreateMode) return this.purchaseUnitStatistics.current;
      const quantity = this.isPackagedProduct ? Number(this.form.packaging.packaged_product_quantity) : 1;
      return quantity > 0 ? Number(this.form.price_purchase) / quantity : null;
    },
    isVisible() {
      return this.productId !== null && this.productId !== undefined;
    },
    hasDifferentSupplierOriginalName() {
      const supplierName = String(this.supplierProduct?.product?.name || '').trim();
      const productName = String(this.form.name || '').trim();
      return Boolean(supplierName) && supplierName.localeCompare(productName, undefined, { sensitivity: 'accent' }) !== 0;
    },
    hasNavigationStack() {
      return this.navigationStack.length > 0;
    },
    previousProductName() {
      const previous = this.navigationStack[this.navigationStack.length - 1];
      return previous?.itemName || previous?.form?.name || "";
    },
    backButtonLabel() {
      return this.previousProductName
        ? `${this.$t("global.back")}: ${this.previousProductName}`
        : this.$t("global.back");
    },
    modalCloseLabel() {
      return this.hasNavigationStack ? this.backButtonLabel : this.$t("global.close");
    },
    cancelButtonLabel() {
      return this.hasNavigationStack ? this.backButtonLabel : this.$t("global.cancel");
    },
    isValid() {
      return !this.$v.form.$invalid;
    },

    productOrderable: {
      get() {
        if (this.form.orderable !== undefined) return this.form.orderable;
        if (this.form.menu && this.form.menus && this.form.menus.length > 0) {
          const mp = this.form.menus.find((m) => m.id === this.form.menu.id);
          if (mp && mp.pivot) return mp.pivot.orderable !== undefined ? mp.pivot.orderable : true;
        }
        return true;
      },
      set(value) {
        this.$set(this.form, "orderable", value);
        if (this.form.menus && this.form.menus.length > 0) {
          const mp = this.form.menus.find((m) => m.id === this.form.menu.id);
          if (mp && mp.pivot) this.$set(mp.pivot, "orderable", value);
        }
      },
    },

    minDate() {
      var from = new Date(this.form.specific_price.from);
      var min_date = new Date();
      min_date.setDate(from.getDate() + 1);
      return min_date.toISOString().slice(0, 10);
    },

    localMessages() {
      return {
        required: this.$t("validation.required_with_attribute"),
        minLength: this.$t("validation.required_min_length_with_attribute"),
      };
    },
    attributes() {
      return {
        name: this.$t("product.product_name"),
        price_currency: this.$t("product.currency"),
        price_amount: this.$t("product.price_amount"),
        "specific_price.reduction": this.$t("product.specific_price.reduction"),
        "specific_price.from": this.$t("product.specific_price.from"),
        menu: this.$t("menu.menu"),
        net_content: this.$t("product.net_content"),
        net_content_unit_code: this.$t("global.measurement_unit"),
        stock_unit_code: this.$t("warehouse.stock_unit"),
      };
    },

    allergenGroups() {
      return allergen_groups.list.sortArray("index");
    },
    stockMeasureCodes() {
      return this.$store.getters["unitCodes/getFilteredUnitCodes"]("measure");
    },
    noProductImage() {
      return "../../../img/no-product-image.png";
    },
    isSupplierProduct() {
      return !!this.supplierProduct;
    },
    isSuppliedProductType() {
      return Boolean(this.form.is_supplied_product);
    },
    suppliedProductId() {
      return this.suppliedProduct?.id || this.form.supplied_product_id || null;
    },
    productWarehouses() {
      return Array.isArray(this.form.warehouses_view) ? this.form.warehouses_view : [];
    },

    isPackagedProduct: {
      get() { return this.form.packaging.is_packaged_product; },
      set(val) { this.form.packaging.is_packaged_product = val; },
    },

    isSupplierManagedPackaging() {
      return Boolean(this.supplierPurchase?.packaging);
    },

    packagingTypeName() {
      if (!this.form.packaging.packaging_type_code) return '';
      const found = this.$globalVar.packagingTypes.find(p => p.value === this.form.packaging.packaging_type_code);
      return found ? found.label : this.form.packaging.packaging_type_code;
    },

    totalNetContent() {
      if (!this.form.net_content || !this.form.packaging.packaged_product_quantity) return 0;
      const total = Number(this.form.net_content) * Number(this.form.packaging.packaged_product_quantity);
      return total % 1 === 0 ? total.toLocaleString() : total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    totalNetContentConverted() {
      if (!this.form.net_content || !this.form.packaging.packaged_product_quantity || !this.form.net_content_unit_code) return null;
      const total = Number(this.form.net_content) * Number(this.form.packaging.packaged_product_quantity);
      return _formatReadableQuantity(total, this.form.net_content_unit_code, this.measureCodes);
    },
  },

  watch: {
    productId: {
      immediate: true,
      handler(id) {
        if (this.restoringNavigationSnapshot) return;

        if (id === "new") {
          this.resetForCreate();
        } else if (id) {
          this.fetchProduct(id);
        }
      },
    },

    activeTab: {
      immediate: true,
      handler(newTab) {
        const normalizedTab = this.normalizeActiveTab(newTab);
        if (normalizedTab !== newTab) {
          this.activeTab = normalizedTab;
          return;
        }
        this.loadTabData(normalizedTab);
      },
    },
  },

  mounted() {
    this.setupPackagingWatcher();
    this.$root.$on("product-quick-detailed-edit", this.handleProductQuickDetailedEdit);
    document.addEventListener("keydown", this.handleKeydown);
  },

  beforeDestroy() {
    this.$root.$off("product-quick-detailed-edit", this.handleProductQuickDetailedEdit);
    document.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    handleKeydown(e) {
      if (e.key === "Escape") this.close();
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        this.update();
      }
    },

    unitCodeLabel(code) {
      return _getUnitCodeLabel(this.measureCodes, code);
    },

    initLoadedTabs() {
      return {
        menus: false,
        cashpadProducts: false,
        warehouses: false,
        extras: false,
        options: false,
        optionGroups: false,
      };
    },

    resetLoadedTabs() {
      this.loadedTabs = this.initLoadedTabs();
    },

    resetAlerts() {
      this.successAlertMessage = {};
      this.dismissSuccessAlert = 0;
      this.errorAlertMessage = {};
      this.showErrorAlert = false;
    },

    prepareForProductLoad() {
      this.loading = true;
      this.form = this.initForm();
      this.supplierProduct = null;
      this.supplierPurchase = null;
      this.suppliedProduct = null;
      this.defaultReceivingWarehouse = null;
      this.initialDefaultReceivingWarehouseId = null;
      this.purchasePrices = null;
      this.weightedPurchasePrice = null;
      this.purchaseUnitStatistics = {};
      this.warehouses = [];
      this.itemName = "";
      this.isReplicated = false;
      this.isDeleted = false;
      this.selectedImage = undefined;
      this.previewImage = this.noProductImage;
      this.editorReady = false;
      this.resetLoadedTabs();
      this.resetAlerts();
    },

    clonePlain(value) {
      return JSON.parse(JSON.stringify(value));
    },

    createNavigationSnapshot() {
      return {
        productId: this.productId,
        form: this.clonePlain(this.form),
        activeTab: this.activeTab,
        itemName: this.itemName,
        supplierProduct: this.clonePlain(this.supplierProduct),
        supplierPurchase: this.clonePlain(this.supplierPurchase),
        suppliedProduct: this.clonePlain(this.suppliedProduct),
        defaultReceivingWarehouse: this.clonePlain(this.defaultReceivingWarehouse),
        initialDefaultReceivingWarehouseId: this.initialDefaultReceivingWarehouseId,
        isDeleted: this.isDeleted,
        isReplicated: this.isReplicated,
        previewImage: this.previewImage,
        selectedImage: this.selectedImage,
        measureCodes: this.clonePlain(this.measureCodes),
        allMenus: this.clonePlain(this.allMenus),
        allExtras: this.clonePlain(this.allExtras),
        allOptions: this.clonePlain(this.allOptions),
        allOptionGroups: this.clonePlain(this.allOptionGroups),
        cashpadProducts: this.clonePlain(this.cashpadProducts),
        warehouses: this.clonePlain(this.warehouses),
        loadedTabs: this.clonePlain(this.loadedTabs),
        purchasePrices: this.clonePlain(this.purchasePrices),
        weightedPurchasePrice: this.weightedPurchasePrice,
        purchaseUnitStatistics: this.clonePlain(this.purchaseUnitStatistics),
      };
    },

    restoreNavigationSnapshot(snapshot) {
      this.restoringNavigationSnapshot = true;
      this.loading = false;
      this.tabLoading = false;
      this.submitted = false;
      this.form = snapshot.form;
      this.activeTab = snapshot.activeTab;
      this.itemName = snapshot.itemName;
      this.supplierProduct = snapshot.supplierProduct;
      this.supplierPurchase = snapshot.supplierPurchase || null;
      this.suppliedProduct = snapshot.suppliedProduct;
      this.defaultReceivingWarehouse = snapshot.defaultReceivingWarehouse;
      this.initialDefaultReceivingWarehouseId = snapshot.initialDefaultReceivingWarehouseId;
      this.isDeleted = snapshot.isDeleted;
      this.isReplicated = snapshot.isReplicated;
      this.previewImage = snapshot.previewImage;
      this.selectedImage = snapshot.selectedImage;
      this.measureCodes = snapshot.measureCodes;
      this.allMenus = snapshot.allMenus;
      this.allExtras = snapshot.allExtras;
      this.allOptions = snapshot.allOptions;
      this.allOptionGroups = snapshot.allOptionGroups;
      this.cashpadProducts = snapshot.cashpadProducts;
      this.warehouses = snapshot.warehouses || [];
      this.loadedTabs = snapshot.loadedTabs;
      this.purchasePrices = snapshot.purchasePrices;
      this.weightedPurchasePrice = snapshot.weightedPurchasePrice;
      this.purchaseUnitStatistics = snapshot.purchaseUnitStatistics || {};
      this.resetAlerts();
      this.editorReady = false;
      this.$store.commit("set", ["productEditModalId", snapshot.productId]);
      this.$nextTick(() => {
        this.editorReady = true;
        this.restoringNavigationSnapshot = false;
      });
    },

    openNestedProduct(productId) {
      const normalizedProductId = parseInt(productId, 10);
      if (!normalizedProductId || Number(this.form.id) === normalizedProductId) return;

      this.navigationStack.push(this.createNavigationSnapshot());
      this.activeTab = 0;
      this.$store.commit("set", ["productEditModalId", normalizedProductId]);
    },

    handleProductQuickDetailedEdit(productId) {
      if (!this.isVisible) return;

      this.openNestedProduct(productId);
    },

    goBackToPreviousProduct() {
      const previous = this.navigationStack.pop();
      if (!previous) return;

      this.restoreNavigationSnapshot(previous);
    },

    closeAfterSuccessfulAction() {
      if (this.hasNavigationStack) {
        this.goBackToPreviousProduct();
        return;
      }

      this.forceClose();
    },

    mergeProductIntoIngredient(currentItem, product) {
      const incomingPivot = Object.prototype.hasOwnProperty.call(product, "pivot") ? product.pivot : undefined;
      const mergedPivot = currentItem.pivot
        ? { ...currentItem.pivot, ...(incomingPivot || {}) }
        : (incomingPivot || null);
      const deletedState = Object.prototype.hasOwnProperty.call(product, "deleted_at")
        ? { is_deleted: product.deleted_at !== null }
        : {};

      return {
        ...currentItem,
        ...product,
        ...deletedState,
        pivot: mergedPivot,
      };
    },

    updateNavigationSnapshotsWithProduct(product) {
      if (!product?.id) return;

      this.navigationStack.forEach((snapshot) => {
        const ingredients = snapshot.form?.product_ingredients;
        if (Array.isArray(ingredients)) {
          const index = ingredients.findIndex((ingredient) => Number(ingredient.id) === Number(product.id));
          if (index > -1) {
            ingredients.splice(index, 1, this.mergeProductIntoIngredient(ingredients[index], product));
          }
        }

        const options = snapshot.form?.product_options;
        if (Array.isArray(options)) {
          const optionIndex = options.findIndex((option) => {
            const optionProductId = option.product_id || option.product?.id;
            return Number(optionProductId) === Number(product.id);
          });

          if (optionIndex > -1) {
            options.splice(optionIndex, 1, this.mergeProductIntoOption(options[optionIndex], product));
          }
        }
      });
    },

    mergeProductIntoOption(currentOption, product) {
      const incomingPivot = Object.prototype.hasOwnProperty.call(product, "pivot") ? product.pivot : undefined;
      const deletedState = Object.prototype.hasOwnProperty.call(product, "deleted_at")
        ? { deleted_at: product.deleted_at }
        : {};

      return {
        ...currentOption,
        product: {
          ...(currentOption.product || {}),
          ...product,
          ...deletedState,
        },
        pivot: currentOption.pivot
          ? { ...currentOption.pivot, ...(incomingPivot || {}) }
          : (incomingPivot || null),
      };
    },

    initForm() {
      return {
        firm_id: this.selectedFirm?.id,
        id: null,
        gtin: null,
        category_default_id: null,
        department_id: 1,
        sku: null,
        ean: null,
        name: "",
        name_short: "",
        description_short: "",
        description: "",

        price_amount: "0.00",
        price_purchase: "0.00",
        price_currency: this.selectedFirm?.default_currency || "EUR",
        tax: null,
        tax_delivery: null,

        net_content: null,
        net_content_unit_code: null,
        stock_unit_code: "H87",

        active: true,
        cook_station: true,
        station_id: null,
        station_conflict: null,
        orderable: true,

        categories: [],
        menu: null,
        menus: [],
        images: [],
        cover_image: null,
        owner_firm_id: null,
        is_supplied_product: false,

        feature: {
          vegetarian: false,
          vegan: false,
          lactose_free: false,
          gluten_free: false,
          bio_mark: false,
          safe_in_pregnancy: true,
          safe_for_drive: true,
          age_restricted: "",
          ingredient: "",
          contains: "",
          storage_instructions: "",
          fbo: "",
          fbo_address: "",
          allergen_information: [],
          energy_cal: "",
          energy_j: "",
          sugars: "",
          fat: "",
          saturated_fat: "",
          carbohydrate: "",
          protein: "",
          salt_equivalent: "",
          fibre: "",
          co2: "",
        },

        specific_price: {
          reduction: null,
          reduction_type: null,
          from: null,
          to: null,
        },

        selectedExtras: [],
        product_ingredients: [],
        product_options: [],

        estimated_price: null,

        packaging: {
          is_packaged_product: false,
          packaging_type_code: null,
          package_product_id: null,
          packaged_product_quantity: null,
          unit_price_purchase: null,
        },

        cashpad_product: null,
        supplied_product_id: null,
        default_stock_warehouse_id: null,
        default_stock_warehouse: null,
        warehouses_view: [],
      };
    },

    async fetchProduct(id) {
      this.prepareForProductLoad();

      try {
        await this.getUnitCodes("measure");

        const response = await this.$axios.get(this.$backend.get("PRODUCT", "SHOW", id));
        const data = response.data;

        // if (this.selectedFirm.id !== data.firm_id) {
        //   this.$store.commit("setSelectedFirm", data.firm);
        // }

        this.form.firm_id = data.firm_id;
        this.form.id = data.id;
        this.form.gtin = data.gtin;
        this.form.category_default_id = data.category_default_id;
        this.form.department_id = data.department_id;
        this.form.sku = data.sku;
        this.form.ean = data.ean;
        this.form.name = data.name;
        this.form.name_short = data.name_short;
        this.form.description_short = data.description_short;
        this.form.description = data.description;

        this.form.price_amount = this.formatPriceAmount(data.price_amount);
        this.form.price_purchase = this.formatPriceAmount(data.price_purchase);
        this.form.estimated_price = data.estimated_price;
        this.form.price_currency = data.price_currency;
        this.weightedPurchasePrice = data.weighted_purchase_price_amount;
        this.purchaseUnitStatistics = mapPurchaseUnitStatistics(data);
        this.form.tax = this.normalizeNullableTaxValue(data.tax);
        this.form.tax_delivery = this.normalizeNullableTaxValue(data.tax_delivery);

        this.form.net_content = data.net_content;
        this.form.net_content_unit_code = data.net_content_unit_code === "1N" ? "H87" : data.net_content_unit_code;
        this.form.stock_unit_code = data.stock_unit_code === "1N" ? "H87" : data.stock_unit_code;

        this.form.active = data.active;
        this.form.cook_station = data.cook_station;
            this.form.station_id = data.station_id ?? null;
            this.form.station_conflict = data.station_conflict ?? null;

        this.form.categories = data.categories;
        this.form.menus = data.menus;
        this.form.menu = data.menus.length > 0 ? data.menus[0] : null;

        if (this.form.menu && this.form.menus.length > 0) {
          const mp = this.form.menus.find((m) => m.id === this.form.menu.id);
          this.form.orderable = mp && mp.pivot ? (mp.pivot.orderable !== undefined ? mp.pivot.orderable : true) : true;
        } else {
          this.form.orderable = true;
        }

        this.form.images = data.images;
        this.form.owner_firm_id = data.owner_firm_id;
        this.form.is_supplied_product = Boolean(data.is_supplied_product);
        this.supplierPurchase = data.supplier_purchase || null;

        // Features
        if (data.feature) {
          const f = data.feature;
          Object.assign(this.form.feature, {
            vegetarian: f.vegetarian,
            vegan: f.vegan,
            lactose_free: f.lactose_free,
            gluten_free: f.gluten_free,
            bio_mark: f.bio_mark,
            safe_in_pregnancy: f.safe_in_pregnancy,
            safe_for_drive: f.safe_for_drive,
            age_restricted: f.age_restricted,
            ingredient: f.ingredient,
            contains: f.contains,
            storage_instructions: f.storage_instructions,
            fbo: f.fbo,
            fbo_address: f.fbo_address,
            energy_cal: f.energy_cal,
            energy_j: f.energy_j,
            sugars: f.sugars,
            fat: f.fat,
            saturated_fat: f.saturated_fat,
            carbohydrate: f.carbohydrate,
            protein: f.protein,
            salt_equivalent: f.salt_equivalent,
            fibre: f.fibre,
            co2: f.co2,
          });

          const allergen_information = (this.form.feature.allergen_information = f.allergen_information);
          if (allergen_information) {
            const intersection = this.allergenGroups.filter(
              (ag) => ag.allergens.some((a) => allergen_information.includes(a)) || allergen_information.includes(ag.value)
            );
            this.form.feature.allergen_information = intersection.map((ag) => ag.value);
          }
        }

        // Cover image
        if (data.cover_image) {
          this.form.cover_image = data.cover_image.url;
          this.previewImage = data.cover_image_url;
        } else {
          this.previewImage = this.noProductImage;
        }

        // Specific price
        if (data.specific_price) {
          this.form.specific_price.reduction_type = data.specific_price.reduction_type;
          this.form.specific_price.reduction = this.formatSpecificPriceReductionValue(
            data.specific_price.reduction,
            data.specific_price.reduction_type
          );
          this.form.specific_price.from = data.specific_price.from;
          this.form.specific_price.to = data.specific_price.to;
        }

        // Extras
        this.form.selectedExtras = data.extras.map((e) => e.id);

        // Ingredients
        if (data.ingredients.length > 0) {
          this.form.product_ingredients = data.ingredients.map((i) => ({
            id: i.id,
            name: i.name,
            name_short: i.name_short,
            net_content: i.net_content,
            net_content_unit_code: i.net_content_unit_code,
            price_purchase: i.price_purchase,
            usage_rate: i.usage_rate,
            estimated_price: i.estimated_price,
            equivalent_products_count: i.equivalent_products_count || 0,
            cook_station: i.cook_station,
            station_id: i.station_id,
            station_conflict: i.station_conflict,
            pivot: i.pivot,
            packaging: i.packaging,
            is_deleted: i.deleted_at !== null,
          }));
        }

        // Options
        if (data.options.length > 0) {
          this.form.product_options = data.options;
        }

        // Packaging
        if ((this.form.packaging.is_packaged_product = !!data.packaging)) {
          this.form.packaging.packaging_type_code = data.packaging.packaging_type_code;
          this.form.packaging.package_product_id = data.packaging.package_product_id;
          this.form.packaging.packaged_product_quantity = data.packaging.packaged_product_quantity;
          this.form.packaging.unit_price_purchase = data.packaging.unit_price_purchase;
        }
        this.applySupplierPackaging();

        // Cashpad
        this.form.cashpad_product = data.cashpad_product;
        this.form.warehouses_view = Array.isArray(data.warehouses_view) ? data.warehouses_view : [];

        this.itemName = data.name;
        this.supplierProduct = data.supplier_product;
        this.applyDefaultReceivingWarehouse(data);
        this.activeTab = this.normalizeActiveTab(this.activeTab);
        this.purchasePrices = data.purchase_prices;
        this.isReplicated = data.owner_firm_id !== null && data.owner_product_id !== null;
        this.isDeleted = data.deleted_at !== null;

        if (this.isRestaurant) {
          this.loadTabData(0, { silent: true });
        }
      } catch (error) {
        console.error(error);
        this.errorAlertMessage = error.response?.data || { message: "Failed to load product" };
        this.showErrorAlert = true;
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.editorReady = true;
          this.$nextTick(() => {
            if (this.$refs.quill_editor_d) {
              this.$refs.quill_editor_d.quill.enable(true);
            }
          });
        });
      }
    },

    applySupplierPackaging() {
      const definition = this.supplierPurchase?.packaging;
      if (!definition) return;
      this.form.packaging.is_packaged_product = definition.is_packaged_product;
      this.form.packaging.packaging_type_code = definition.packaging_type_code;
      this.form.packaging.packaged_product_quantity = definition.packaged_product_quantity;
      this.form.packaging.unit_price_purchase = definition.is_packaged_product
        ? this.purchaseUnitStatistics.current ?? null : null;
    },

    setupPackagingWatcher() {
      this.$watch(
        (vm) => [vm.isPackagedProduct, vm.form.packaging.packaged_product_quantity],
        () => {
          if (this.isPackagedProduct) {
            if (!this.isSupplierManagedPackaging) this.form.stock_unit_code = "H87";
            if (this.form.packaging.packaged_product_quantity > 0) {
              this.form.packaging.unit_price_purchase = this.unitPurchasePrice ?? null;
            }
          }
        },
        { immediate: false, deep: true }
      );
    },

    restaurantTabIndex(tabName) {
      const map = { ingredients: 4, extras: 5, options: 6, equivalents: 7 };
      return map[tabName];
    },
    normalizeActiveTab(tabIndex) {
      if (this.isSupplierProduct && this.isRestrictedRestaurantTab(tabIndex)) {
        return 0;
      }
      return tabIndex;
    },
    isRestrictedRestaurantTab(tabIndex) {
      const blockedTabs = [this.restaurantTabIndex("ingredients")];
      return blockedTabs.includes(tabIndex);
    },

    checkIfValid(fieldName) {
      const fields = fieldName.split(".");
      const field = fields.length > 1 ? this.$v.form[fields[0]][fields[1]] : this.$v.form[fieldName];
      if (!field.$dirty) return null;
      return !(field.$invalid || field.$model === "");
    },

    onFileChanged(files) {
      this.selectedImage = files[0];
      this.$v.form.cover_image.$model = this.selectedImage.name;

      if (this.checkIfValid("cover_image")) {
        this.previewImage = URL.createObjectURL(this.selectedImage);
      } else {
        this.selectedImage = undefined;
        this.previewImage = undefined;
        this.$v.form.cover_image.$model = "";
      }
    },

    async update() {
      if (this.isValid) {
        this.submitted = true;

        try {
          if (this.selectedImage) {
            const formData = new FormData();
            formData.append("cover_image", this.selectedImage, this.selectedImage.name);
            const imgRes = await this.$axios.post(this.$backend.get("PRODUCT", "UPLOAD_IMAGE"), formData);
            this.$v.form.cover_image.$model = imgRes.data.data.cover_image;
            this.selectedImage = null;
          }

          if (this.isCreateMode) {
            await this.storeProduct();
          } else {
            await this.updateProduct();
          }
        } catch (error) {
          this.errorAlertMessage = error.response?.data || { message: this.isCreateMode ? "Create failed" : "Update failed" };
          this.showErrorAlert = true;
          this.submitted = false;
        }
      } else {
        this.$v.$touch();
        this.activeTab = 0;
      }
    },

    async updateProduct() {
      try {
        const response = await this.$axios.put(
          this.$backend.get("PRODUCT", "UPDATE", this.form.id),
          this.buildProductPayload()
        );
        await this.updateDefaultReceivingWarehouseIfNeeded();
        response.data.supplied_product_id = this.suppliedProductId;
        response.data.default_stock_warehouse_id = this.initialDefaultReceivingWarehouseId;
        response.data.default_stock_warehouse = this.defaultReceivingWarehouse;
        this.itemName = response.data.name;
        this.successAlertMessage = {
          itemName: this.itemName,
          message: `${this.$t("global.updated")}.`,
        };
        this.dismissSuccessAlert = this.dismissSecs;
        this.showErrorAlert = false;
        this.isDeleted = false;

        this.$emit("saved", response.data);
        this.updateNavigationSnapshotsWithProduct(response.data);

        setTimeout(() => this.closeAfterSuccessfulAction(), 1500);
      } catch (error) {
        this.errorAlertMessage = error.response?.data || { message: "Update failed" };
        this.showErrorAlert = true;
      } finally {
        this.submitted = false;
      }
    },

    deleteItem() {
      this.$confirm({
        title: this.$t("global.confirm"),
        message: `⚠️ ${this.$t("global.confirm_delete")}`,
        button: {
          no: this.$t("global.no"),
          yes: this.$t("global.yes"),
        },
        callback: (confirm) => {
          if (confirm) {
            this.submitted = true;
            this.$axios
              .delete(this.$backend.get("PRODUCT", "DELETE", this.form.id))
              .then(() => {
                this.showErrorAlert = false;
                this.successAlertMessage = {
                  itemName: this.form.name,
                  message: `${this.$t("global.deleted")}.`,
                };
                this.dismissSuccessAlert = this.dismissSecs;
                this.updateNavigationSnapshotsWithProduct({ id: this.form.id, deleted_at: new Date().toISOString() });
                this.$emit("deleted", this.form.id);
                setTimeout(() => this.closeAfterSuccessfulAction(), 1500);
              })
              .catch((error) => {
                this.errorAlertMessage = error.response.data;
                this.showErrorAlert = true;
              })
              .finally(() => {
                this.submitted = false;
              });
          }
        },
      });
    },

    close() {
      if (this.hasNavigationStack) {
        this.goBackToPreviousProduct();
        return;
      }

      this.forceClose();
    },

    forceClose() {
      this.navigationStack = [];
      this.$store.commit("set", ["productEditModalId", null]);
      this.$store.commit("set", ["productEditModalMenuId", null]);
    },

    applyCreateModeMenuPreselection() {
      const menuId = this.$store.state.productEditModalMenuId;
      if (menuId && this.allMenus.length > 0) {
        const menu = this.allMenus.find((m) => m.id === menuId);
        if (menu) {
          this.form.menu = menu;
          this.form.menus = [menu];
          this.form.orderable = true;
        }
      }
    },

    async loadTabData(tabIndex, options = {}) {
      const { silent = false } = options;
      if (!this.isRestaurant) return;
      if (this.isSupplierProduct && this.isRestrictedRestaurantTab(tabIndex)) return;

      const TAB_EXTRAS = 5;
      const TAB_OPTIONS = 6;
      const needsLoad = [];

      if (tabIndex === 0) {
        if (this.isSuppliedProductType && !this.loadedTabs.warehouses) {
          needsLoad.push(this.getWarehouses().then(() => (this.loadedTabs.warehouses = true)));
        }
        if (!this.loadedTabs.menus) {
          needsLoad.push(
            this.getMenus().then(() => {
              this.loadedTabs.menus = true;
              if (this.isCreateMode) this.applyCreateModeMenuPreselection();
            })
          );
        }
        if (!this.loadedTabs.cashpadProducts) {
          needsLoad.push(this.getCashpadProducts().then(() => (this.loadedTabs.cashpadProducts = true)));
        }
      } else if (tabIndex === TAB_EXTRAS) {
        if (!this.loadedTabs.extras) needsLoad.push(this.getExtras().then(() => (this.loadedTabs.extras = true)));
      } else if (tabIndex === TAB_OPTIONS) {
        if (!this.loadedTabs.options) needsLoad.push(this.getOptions().then(() => (this.loadedTabs.options = true)));
        if (!this.loadedTabs.optionGroups) needsLoad.push(this.getOptionGroups().then(() => (this.loadedTabs.optionGroups = true)));
      }

      if (needsLoad.length > 0) {
        if (!silent) this.tabLoading = true;
        try {
          await Promise.all(needsLoad);
        } catch (e) {
          console.error(e);
        } finally {
          if (!silent) this.tabLoading = false;
        }
      }
    },

    async resetForCreate() {
      this.loading = true;
      this.form = this.initForm();
      this.supplierProduct = null;
      this.supplierPurchase = null;
      this.purchaseUnitStatistics = {};
      this.suppliedProduct = null;
      this.defaultReceivingWarehouse = null;
      this.initialDefaultReceivingWarehouseId = null;
      this.activeTab = 0;
      this.resetLoadedTabs();
      this.resetAlerts();

      try {
        await this.getUnitCodes("measure");

        this.itemName = "";
        this.isReplicated = false;
        this.isDeleted = false;
        this.previewImage = this.noProductImage;
        this.editorReady = true;
      } catch (error) {
        console.error(error);
        this.errorAlertMessage = error.response?.data || { message: "Failed to load form" };
        this.showErrorAlert = true;
      } finally {
        this.loading = false;
      }
    },

    async storeProduct() {
      try {
        const response = await this.$axios.post(
          this.$backend.get("PRODUCT", "INSERT"),
          this.buildProductPayload()
        );
        this.itemName = response.data.name;
        this.successAlertMessage = {
          itemName: this.itemName,
          message: `${this.$t("global.added")}.`,
        };
        this.dismissSuccessAlert = this.dismissSecs;
        this.showErrorAlert = false;

        this.$emit("saved", response.data);

        setTimeout(() => this.closeAfterSuccessfulAction(), 1500);
      } catch (error) {
        this.errorAlertMessage = error.response?.data || { message: "Create failed" };
        this.showErrorAlert = true;
      } finally {
        this.submitted = false;
      }
    },
    onUpdateShow(show) {
      if (!show) this.close();
    },

    normalizeNullableTaxValue(value) {
      return value === null || value === "" || value === "0.00" || value === "null" || value === 0 || value === 0.0 || value === "0" ? null : value;
    },

    formatPriceAmount(value) {
      if (value === null || value === undefined || value === "") return value;

      const amount = Number(value);
      return Number.isFinite(amount) ? amount.toFixed(2) : value;
    },

    formatPriceField(field) {
      this.$v.form[field].$model = this.formatPriceAmount(this.form[field]);
    },

    formatSpecificPriceReductionValue(value, reductionType = this.form.specific_price.reduction_type) {
      return reductionType === "amount" ? this.formatPriceAmount(value) : value;
    },

    formatSpecificPriceReduction() {
      this.$v.form.specific_price.reduction.$model = this.formatSpecificPriceReductionValue(
        this.form.specific_price.reduction
      );
    },

    buildProductPayload() {
      const payload = { ...this.form };
      delete payload.warehouses_view;
      delete payload.default_stock_warehouse_id;
      delete payload.default_stock_warehouse;
      // Purchase cost is read-only here and is managed by approved receiving flows.
      delete payload.price_purchase;

      return {
        ...payload,
        tax: this.normalizeNullableTaxValue(this.form.tax),
        tax_delivery: this.normalizeNullableTaxValue(this.form.tax_delivery),
      };
    },

    applyDefaultReceivingWarehouse(product) {
      this.suppliedProduct = product.supplied_product || null;
      this.form.supplied_product_id = this.suppliedProduct?.id || null;
      this.form.default_stock_warehouse_id = product.default_stock_warehouse_id || null;
      this.form.default_stock_warehouse = product.default_stock_warehouse || null;
      this.initialDefaultReceivingWarehouseId = product.default_stock_warehouse_id || null;
      this.defaultReceivingWarehouse = product.default_stock_warehouse || null;
      this.syncDefaultReceivingWarehouseFromOptions();
    },

    syncDefaultReceivingWarehouseFromOptions() {
      const warehouseId = this.defaultReceivingWarehouse?.id || this.initialDefaultReceivingWarehouseId;
      if (!warehouseId || !this.warehouses.length) {
        return;
      }

      const option = this.warehouses.find((warehouse) => Number(warehouse.id) === Number(warehouseId));
      if (option) {
        this.defaultReceivingWarehouse = option;
      }
    },

    defaultReceivingWarehouseChanged() {
      const currentId = this.defaultReceivingWarehouse?.id || null;
      const initialId = this.initialDefaultReceivingWarehouseId || null;

      return Number(currentId || 0) !== Number(initialId || 0);
    },

    isDefaultReceivingWarehouse(warehouse) {
      return Number(warehouse?.id) === Number(this.defaultReceivingWarehouse?.id || 0);
    },

    warehousePiece(warehouse) {
      const piece = parseFloat(warehouse?.pivot?.piece ?? 0);
      return Number.isFinite(piece) ? piece.toFixed(2).replace(/\.00$/, "") : "0";
    },

    async updateDefaultReceivingWarehouseIfNeeded() {
      if (!this.form.id || !this.defaultReceivingWarehouseChanged()) {
        return;
      }

      if (!this.defaultReceivingWarehouse?.id) {
        return;
      }

      const url = this.$backend
        .get("PRODUCT", "UPDATE_DEFAULT_STOCK_WAREHOUSE")
        .replace("{firm_id}", this.selectedFirm.id)
        .replace("{product_id}", this.form.id);

      const response = await this.$axios.put(url, {
        warehouse_id: this.defaultReceivingWarehouse.id,
      });

      this.initialDefaultReceivingWarehouseId = response.data.default_stock_warehouse_id;
      this.defaultReceivingWarehouse = response.data.default_stock_warehouse || this.defaultReceivingWarehouse;
      this.form.default_stock_warehouse_id = response.data.default_stock_warehouse_id;
      this.form.default_stock_warehouse = this.defaultReceivingWarehouse;
    },

    async getMenus() {
      const url = new URL(api.restaurants.menus.list(this.selectedFirm.id));
      const response = await this.$axios.get(url);
      this.allMenus = response.data;
    },
    async getExtras() {
      const response = await this.$axios.get(api.restaurants.extras.list(this.selectedFirm.id));
      this.allExtras = response.data;
    },
    async getOptions() {
      const response = await this.$axios.get(api.restaurants.options.list(this.selectedFirm.id));
      this.allOptions = response.data;
    },
    async getOptionGroups() {
      const response = await this.$axios.get(api.restaurants.options.groups.list(this.selectedFirm.id));
      this.allOptionGroups = response.data;
    },
    async getUnitCodes(type) {
      this.measureCodes = await this.$store.dispatch("unitCodes/fetchUnitCodes", type);
    },
    async getCashpadProducts() {
      const response = await this.$axios.get(
        this.$backend.get("CASHPAD", "GET_PRODUCTS").replace("{firm_id}", this.selectedFirm.id),
        { params: { fields: "product_id,cashpad_product_id" } }
      );
      this.cashpadProducts = response.data;
    },
    async getWarehouses() {
      const response = await this.$axios.get(
        this.$backend.get("WAREHOUSE", "LIST").replace("{firm_id}", this.selectedFirm.id)
      );
      this.warehouses = Array.isArray(response.data) ? response.data : [];
      this.syncDefaultReceivingWarehouseFromOptions();
    },
  },
};
</script>

<style scoped>
/* ═══════════════ LAYOUT ═══════════════ */
.pem-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  padding: 0.5rem;
}

/* ═══════════════ HEADER ═══════════════ */
.pem-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
}
.pem-header__info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  flex: 1 1 auto;
}
.pem-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
}
.pem-header__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.pem-badge {
  font-size: 0.75rem;
  font-weight: 500;
}
.pem-badge--renamed {
  cursor: help;
  display: inline-flex;
  align-items: center;
}
.pem-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;
}
.pem-header__close:hover {
  background: rgba(255, 255, 255, 0.3);
}
.pem-header__close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

/* ═══════════════ LOADING ═══════════════ */
.pem-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 200px;
  gap: 1rem;
}
.pem-loading__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e9ecef;
  border-top-color: #321fdb;
  border-radius: 50%;
  animation: pem-spin 0.8s linear infinite;
}
@keyframes pem-spin {
  to { transform: rotate(360deg); }
}
.pem-loading__text {
  color: #6c757d;
  font-size: 0.875rem;
}

/* Tab loading overlay */
.pem-tab-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  z-index: 5;
  border-radius: 0.375rem;
}
.pem-fade-enter-active,
.pem-fade-leave-active {
  transition: opacity 0.2s;
}
.pem-fade-enter,
.pem-fade-leave-to {
  opacity: 0;
}

/* ═══════════════ INFO BAR ═══════════════ */
.pem-info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  padding: 0.625rem 0.875rem;
  background: #e8f4fd;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
}
.pem-info-item strong {
  color: #495057;
}

/* ═══════════════ TOGGLES ═══════════════ */
.pem-toggles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0.875rem;
  background: linear-gradient(180deg, #f8faff 0%, #f4f6fb 100%);
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  margin-bottom: 0.75rem;
}
.pem-toggle-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.625rem;
  background: rgba(255,255,255,0.7);
}
.pem-station-field {
  flex: 1 1 15rem;
  max-width: 26rem;
  margin-bottom: 0;
  margin-left: auto;
}

/* ═══════════════ FORM LAYOUT ═══════════════ */
.pem-section {
  padding: 0.25rem 0;
}
.pem-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.pem-row > .pem-field {
  flex: 1 1 0;
  min-width: 0;
}
.pem-row--half {
  max-width: 50%;
}
.pem-row--fluid {
  max-width: none;
  margin-bottom: 0;
}
.pem-row--quad > .pem-field {
  flex: 1 1 0;
  min-width: 0;
}
.pem-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

/* ═══════════════ WAREHOUSE ═══════════════ */
.pem-warehouse {
  border: 2px solid #2eb85c;
  padding: 1.25rem;
  border-radius: 0.5rem;
  background: #f8f9fa;
  margin-top: 0.75rem;
}
.pem-warehouse__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.pem-warehouse__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}
.pem-warehouse__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: start;
}
.pem-warehouse__grid--basic {
  grid-template-columns: 1fr;
}
.pem-warehouse-panel {
  min-width: 0;
  border: 1px solid #dce5ef;
  border-radius: 8px;
  background: #fff;
  padding: 0.75rem;
}
.pem-warehouse-panel--content .pem-row--fluid {
  gap: 0.75rem;
}
.pem-warehouse-panel--content .pem-row--fluid > .pem-field {
  margin-bottom: 0;
}
.pem-warehouse__column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
.pem-warehouse__column > .pem-warehouse-purchase {
  min-width: 0;
  margin-bottom: 0;
}
.pem-warehouse-purchase__help {
  display: block;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dce8f2;
  line-height: 1.45;
}
.pem-warehouse-panel--default {
  border-color: #b8dcff;
  background: #f8fbff;
}
.pem-warehouse-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.pem-warehouse-panel__header span {
  display: block;
  color: #263a5b;
  font-size: 0.8rem;
  font-weight: 700;
}
.pem-warehouse-panel__header small {
  display: block;
  color: #768192;
  font-size: 0.72rem;
  line-height: 1.25;
  margin-top: 0.1rem;
}
.pem-default-receiving {
  margin-bottom: 0;
}
.pem-default-receiving__warehouses {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
}
.pem-default-receiving__warehouse-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 100%;
  min-height: 26px;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  background: #fff;
  color: #3c4b64;
  padding: 0.2rem 0.45rem;
  font-size: 0.74rem;
}
.pem-default-receiving__warehouse-chip strong {
  margin-left: auto;
}
.pem-default-receiving__warehouse-chip--default {
  border-color: #80bdff;
  background: #eef6ff;
  color: #1f64c8;
}
.pem-default-receiving__warehouse-name {
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pem-warehouse-option {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  max-width: 100%;
}
.pem-warehouse-option span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pem-warehouse .pem-packaging {
  margin-bottom: 0;
}

/* ═══════════════ PACKAGING ═══════════════ */
.pem-packaging {
  background: #e8f5e9;
  border-radius: 0.375rem;
  border: 1px solid #c8e6c9;
  padding: 1rem;
  margin-bottom: 1rem;
}
.pem-packaging__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

/* ═══════════════ PACKAGING SUMMARY ═══════════════ */
.pem-packaging-summary {
  margin-top: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%);
  border: 1px solid #ffe082;
  border-radius: 0.375rem;
}
.pem-packaging-summary__title {
  margin: 0 0 0.375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #856404;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.pem-packaging-summary__grid {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.pem-packaging-summary__item {
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
}
.pem-packaging-summary__label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #856404;
}
.pem-packaging-summary__value {
  font-size: 0.875rem;
  color: #333;
}

/* ═══════════════ SPECIFIC PRICE ═══════════════ */
.pem-specific-price {
  background: #f8f0dfbb;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}
.pem-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
}

.pem-purchase-cost {
  padding: 0.75rem;
  border: 1px solid #dce8f2;
  border-radius: 0.375rem;
  background: #f4f8fc;
}
.pem-purchase-cost__value {
  display: block;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  margin-bottom: 0.25rem;
}

/* ═══════════════ FEATURES ═══════════════ */
.pem-feature-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}
.pem-feature-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* ═══════════════ EXTRAS ═══════════════ */
.pem-extra-group__title {
  text-transform: uppercase;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.pem-extra-list {
  display: flex;
  flex-wrap: wrap;
  list-style-type: none;
  background-color: #e6e6fa;
  border-radius: 0.375rem;
  margin: 0 0 1rem;
  padding: 0.625rem 1rem;
}
.pem-extra-list li {
  flex: 0 0 100%;
}
.pem-extra-label {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.pem-extra-label input[type="checkbox"] {
  cursor: pointer;
}
.pem-extra--disabled {
  opacity: 0.7;
}
.pem-extra--disabled .pem-extra-label input[type="checkbox"]:checked + span {
  text-decoration: line-through;
}
.pem-extra--product-deleted {
  opacity: 0.65;
  background: #fff5f5;
  border-radius: 4px;
  padding: 0 0.25rem;
}
.pem-extra--product-deleted .pem-extra-label span {
  text-decoration: line-through;
  color: #999;
}
.pem-extra-deleted-badge {
  font-size: 0.62rem;
  padding: 0.1em 0.38em;
  vertical-align: middle;
}

/* ═══════════════ FOOTER ═══════════════ */
.pem-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
}
.pem-footer__actions {
  display: flex;
  gap: 0.5rem;
}
.pem-footer__danger {
  margin-left: auto;
}

/* ═══════════════ RESPONSIVE ═══════════════ */
@media (max-width: 1199px) {
  .pem-row {
    flex-direction: column;
    gap: 0;
  }
  .pem-row--half {
    max-width: 100%;
  }
  .pem-row--quad {
    flex-direction: column;
    gap: 0;
  }
  .pem-feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .pem-info-bar {
    flex-direction: column;
    gap: 0.25rem;
  }
  .pem-footer {
    flex-wrap: wrap;
  }
  .pem-warehouse__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .pem-body {
    padding: 0.25rem;
  }
  .pem-header__title {
    font-size: 0.875rem;
  }
  .pem-tab-label {
    display: none;
  }
  .pem-extra-list {
    max-height: 100% !important;
  }
  .pem-warehouse {
    padding: 0.75rem;
  }
  .pem-warehouse__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.65rem;
  }
  .pem-warehouse__header .pem-toggle-item {
    width: 100%;
    justify-content: space-between;
  }
  .pem-warehouse-panel {
    padding: 0.65rem;
  }
  .pem-default-receiving__warehouse-chip {
    width: 100%;
  }
  .pem-default-receiving__warehouse-name {
    flex: 1 1 auto;
    max-width: none;
  }
}

@media (min-width: 768px) {
  .pem-extra-list {
    flex-direction: column;
  }
  .pem-extra-list li {
    flex: 1 1 1rem;
  }
}

@media (min-width: 1200px) {
  .pem-row {
    flex-direction: row;
    gap: 0.75rem;
  }
  .pem-row--quad {
    flex-direction: row;
    gap: 0.75rem;
  }
  .pem-feature-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>

<style>
/* ═══════════════ MODAL STRUCTURE ═══════════════ */
.product-edit-modal .modal-content {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.product-edit-modal .modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.875rem 1.25rem;
  flex-shrink: 0;
}
.product-edit-modal .modal-body {
  padding: 0.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.product-edit-modal .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 0.5rem;
  background: #f8f9fa;
  flex-shrink: 0;
}

/* ═══════════════ FORM / TABS ═══════════════ */
.product-edit-modal .pem-form {
  position: relative;
}
.product-edit-modal .pem-tabs .nav-pills {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 0.25rem;
  padding: 0.5rem 0;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  background: #fff;
  position: relative;
  z-index: 1;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.product-edit-modal .pem-tabs .nav-pills .nav-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  white-space: nowrap;
  font-size: 0.8125rem;
  padding: 0.375rem 0.75rem;
  border-radius: 2rem;
  transition: all 0.2s;
}

/* iOS Safari'de hizli dikey scroll sirasinda tab satirinda gorunen beyaz clipping/flicker sorununu azaltir. */
@supports (-webkit-touch-callout: none) {
  /*
   * iOS Safari sometimes stops painting the caret when an editable control is
   * inside nested momentum-scroll layers. Keep normal scrolling while a field
   * is focused so the caret remains in the actively painted layer.
   */
  .product-edit-modal .modal-body:focus-within,
  .product-edit-modal .pem-body:focus-within {
    -webkit-overflow-scrolling: auto;
  }

  .product-edit-modal input:not([type="checkbox"]):not([type="radio"]),
  .product-edit-modal textarea,
  .product-edit-modal [contenteditable="true"],
  .product-edit-modal [contenteditable=""] {
    -webkit-user-select: text !important;
    user-select: text !important;
    touch-action: auto;
    caret-color: currentColor !important;
    -webkit-text-fill-color: currentColor;
  }

  .product-edit-modal .pem-tabs .nav-pills {
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  .product-edit-modal .pem-tabs .nav-pills .nav-item,
  .product-edit-modal .pem-tabs .nav-pills .nav-link {
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
}

/* ═══════════════ MEASUREMENT & ORDER CODES ═══════════════ */
.product-edit-modal .mcode-group {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: repeat(auto-fit, minmax(80px, 80px));
}
.product-edit-modal .mcode-group .form-check-label {
  text-align: center;
  width: 80px;
  cursor: pointer;
  padding: 0.25rem;
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-radius: 4px;
  font-size: 0.8125rem;
}
.product-edit-modal .mcode-group input[type="radio"]:checked + .form-check-label {
  color: #0a0b18;
  background-color: #c4ebd1aa;
  border-color: #c4ebd1;
}
.product-edit-modal .od-group {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: repeat(auto-fit, minmax(110px, 110px));
}
.product-edit-modal .od-group .form-check-label {
  text-align: center;
  width: 110px;
  cursor: pointer;
  padding: 0.25rem;
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-radius: 4px;
  font-size: 0.8125rem;
}
.product-edit-modal .od-group input[type="radio"]:checked + .form-check-label {
  color: #0a0b18;
  background-color: #c4ebd1aa;
  border-color: #c4ebd1;
}

/* Disable items */
.product-edit-modal .disable-item {
  pointer-events: none;
  opacity: 0.5;
}

/* ═══════════════ RESPONSIVE: < 1200px → Fullscreen ═══════════════ */
@media (max-width: 1199px) {
  .product-edit-modal .modal-dialog {
    max-width: 100%;
    width: 100%;
    margin: 0;
    height: 100vh;
    max-height: 100vh;
  }
  .product-edit-modal .modal-content {
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
}

/* ═══════════════ RESPONSIVE: >= 1200px → 90% viewport ═══════════════ */
@media (min-width: 1200px) {
  .product-edit-modal .modal-dialog {
    display: flex;
    align-items: center;
    min-height: 100vh;
    margin: auto;
  }
  .product-edit-modal .modal-dialog.modal-xl {
    max-width: 90vw;
  }
  .product-edit-modal .modal-content {
    width: 100%;

    /* >= 1200px modal yüksekliği:
       Eski: max-height: 90vh;
       Yeni: height: 90vh (viewportun %90'ı kadar sabit yükseklikte)
       İçerik az olsa da çok olsa da yükseklik değişmez,
       fazla içerikte .pem-body scroll olur.
    */
    /* max-height: 90vh; */
    height: 90vh;
  }
}
</style>
